import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Brain, Send, User, Bot, AlertCircle, Paperclip, Camera, Image as ImageIcon, FileText, X, Copy, Check as CheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from './SEOHead';
import MermaidDiagram from './MermaidDiagram';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAdvisor = () => {
  const { user, openAuth, fetchUser } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [queriesLeft, setQueriesLeft] = useState(10);
  const [hasUnlimitedAccess, setHasUnlimitedAccess] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus input when not loading
  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading]);

  // Generate a stable unique ID for messages
  const genMsgId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // Load conversation history on mount (when user is logged in)
  useEffect(() => {
    if (user?.id && user?.profile?.completed_questionnaire) {
      loadHistory();
    }
  }, [user?.id]);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/ai/advisor-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map(m => ({
          id: m.message_id,
          text: m.text,
          sender: m.sender,
          timestamp: new Date(m.timestamp),
          attachment: m.attachment || null,
          images: m.images || [],
          mermaidDiagrams: m.mermaid_diagrams || []
        })));
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (e) {
      // Silently fail - history is optional
    }
  };

  const deleteMessage = async (messageId) => {
    setDeletingId(messageId);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/ai/advisor-history/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (e) {
      toast.error('Failed to delete message');
    }
    setDeletingId(null);
  };

  const copyMessage = (messageId, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => toast.error('Could not copy text'));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check if user has unlimited access (admin or active AI Buddy subscription)
    if (user) {
      const isAdmin = user.email === "shifracs@gmail.com";
      const hasAIBuddyAccess = user.ai_buddy_expiry && 
        new Date(user.ai_buddy_expiry) > new Date();
      
      const unlimited = isAdmin || hasAIBuddyAccess;
      setHasUnlimitedAccess(unlimited);
      
      if (!unlimited) {
        // Free tier users: calculate queries left based on weekly limit
        const queriesUsed = user.ai_queries_this_week || 0;
        setQueriesLeft(3 - queriesUsed); // Free tier has 3 queries per week
      }
    }
  }, [user]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachedFile(file);
      setShowAttachmentMenu(false);
      toast.success(`File attached: ${file.name}`);
    }
  };

  const sendMessage = async () => {
    if ((!inputText.trim() && !attachedFile) || loading) return;

    if (!user) {
      openAuth('register');
      return;
    }

    if (!user?.profile?.completed_questionnaire) {
      toast.error('Please complete your profile first to use the AI advisor');
      return;
    }

    // Generate stable IDs for both messages (used for history persistence)
    const userMsgId = genMsgId();
    const aiMsgId = genMsgId();

    const userMessage = {
      id: userMsgId,
      text: inputText || (attachedFile ? `Attached: ${attachedFile.name}` : ''),
      sender: 'user',
      timestamp: new Date(),
      attachment: attachedFile ? {name: attachedFile.name, type: attachedFile.type} : null
    };

    setMessages(prev => [...prev, userMessage]);
    const currentQuery = inputText;
    const currentFile = attachedFile;
    const userMsgTimestamp = new Date().toISOString();
    setInputText('');
    setAttachedFile(null);
    setLoading(true);

    // Create a placeholder AI message that will be updated with streaming response
    const aiMessage = {
      id: aiMsgId,
      text: '',
      sender: 'ai',
      timestamp: new Date(),
      images: [],
      mermaidDiagrams: []
    };
    
    setMessages(prev => [...prev, aiMessage]);

    try {
      const token = localStorage.getItem('token');
      
      // ALWAYS use FormData (backend expects Form(...) parameters)
      const formData = new FormData();
      
      // Append query text (required or optional with file)
      if (currentQuery && currentQuery.trim()) {
        formData.append('query', currentQuery.trim());
      }
      
      // Append file if present
      if (currentFile) {
        formData.append('file', currentFile);
      }
      
      const response = await fetch(`${API}/ai/query-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get AI response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      // Track extra data for history save
      let accumulatedImages = [];
      let accumulatedMermaid = [];
      let savedFileAttachment = userMessage.attachment;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                toast.error(data.chunk);
                accumulatedText = data.chunk;
                break;
              }
              
              if (data.chunk) {
                accumulatedText += data.chunk;
                
                // Update the AI message with accumulated text
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMsgId 
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));
              }
              
              // Handle image generation
              if (data.type === 'image' && data.url) {
                accumulatedImages.push(data.url);
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMsgId 
                    ? { ...msg, images: [...(msg.images || []), data.url] }
                    : msg
                ));
              }
              
              // Handle mermaid diagrams
              if (data.type === 'mermaid' && data.code) {
                accumulatedMermaid.push(data.code);
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMsgId 
                    ? { ...msg, mermaidDiagrams: [...(msg.mermaidDiagrams || []), data.code] }
                    : msg
                ));
              }
              
              // Handle text cleanup (remove code blocks from displayed text)
              if (data.type === 'text_cleanup' && data.text) {
                accumulatedText = data.text;
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMsgId 
                    ? { ...msg, text: data.text }
                    : msg
                ));
              }
              
              // Handle file upload confirmation - update the user message with file URL
              if (data.type === 'file_uploaded' && data.url) {
                savedFileAttachment = {
                  name: currentFile?.name,
                  type: currentFile?.type,
                  url: data.url,
                  backend_url: `${BACKEND_URL}${data.url}`
                };
                setMessages(prev => prev.map(msg => 
                  msg.id === userMsgId && msg.sender === 'user'
                    ? { 
                        ...msg, 
                        attachment: savedFileAttachment
                      }
                    : msg
                ));
              }
              
              if (data.done) {
                // Deduct 2 credits after successful AI response
                try {
                  const token = localStorage.getItem('token');
                  await fetch(`${API}/credits/deduct`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ action: 'ask_nerd_dashboard' })
                  });
                  // Trigger live credit update
                  window.dispatchEvent(new Event('creditUpdated'));
                  
                  // Refetch user profile to update query counter
                  if (fetchUser) {
                    fetchUser().catch(err => console.error('Failed to refetch profile:', err));
                  }
                } catch (creditError) {
                  console.error('Credit deduction failed:', creditError);
                }

                // Save both messages to persistent history
                try {
                  const token = localStorage.getItem('token');
                  await fetch(`${API}/ai/advisor-save`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      messages: [
                        {
                          message_id: userMsgId,
                          sender: 'user',
                          text: currentQuery || (currentFile ? `Attached: ${currentFile.name}` : ''),
                          timestamp: userMsgTimestamp,
                          attachment: savedFileAttachment,
                          images: [],
                          mermaid_diagrams: []
                        },
                        {
                          message_id: aiMsgId,
                          sender: 'ai',
                          text: accumulatedText,
                          timestamp: new Date().toISOString(),
                          attachment: null,
                          images: accumulatedImages,
                          mermaid_diagrams: accumulatedMermaid
                        }
                      ]
                    })
                  });
                } catch (saveError) {
                  console.error('Failed to save history:', saveError);
                }

                break;
              }
            } catch (e) {
              // Ignore JSON parse errors from incomplete chunks
            }
          }
        }
      }

    } catch (error) {
      const errorMessage = error.message || 'Failed to get AI response';
      toast.error(errorMessage);
      
      // Remove the placeholder AI message on error
      setMessages(prev => prev.filter(msg => msg.id !== aiMsgId));
      
      if (error.message?.includes('429')) {
        setQueriesLeft(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Old handleSendMessage function removed - replaced with sendMessage above

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-viewport fixed top-0 left-0 right-0 bg-gray-900 flex flex-col overflow-hidden" style={{ touchAction: 'none' }}>
      <SEOHead title="Career Advisor AI" description="Get personalised AI-powered career guidance, skill gap analysis and study roadmaps tailored to your ambitions." path="/ai-advisor" />
      <div className="flex-1 overflow-y-auto overflow-x-hidden pt-20 min-h-0" style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
        <div className="max-w-3xl mx-auto px-2 sm:px-4">
          {/* Chat Messages */}
          <div className="px-2 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="text-center py-8 sm:py-16 px-2">
                <div className="flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                  <img 
                    src="/nerd-nation-logo.png" 
                    alt="Nerd Nation" 
                    className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                    style={{ filter: 'none' }} loading="lazy" decoding="async"/>
                </div>
                <h3 className="text-lg sm:text-xl font-bold gradient-text-gold font-outfit mb-2">Ask Nerd AI</h3>
                      <p className="text-sm sm:text-base text-white font-outfit max-w-md mx-auto mb-4 sm:mb-6">
                        Your Learning Companion! 🌍
                      </p>
                      <p className="text-xs sm:text-sm text-white font-outfit max-w-lg mx-auto px-2">
                        Ask me about your career path, exam preparation, and academic queries!
                      </p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} group`}
                        data-testid={`message-${message.sender}`}
                      >
                        <div className={`flex items-start space-x-3 max-w-[90%] md:max-w-2xl ${
                          message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                        }`}>
                          {message.sender === 'user' ? (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-800">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gold-primary/20 flex items-center justify-center flex-shrink-0">
                              <img 
                                src="/nerd-nation-logo.png" 
                                alt="Nerd Nation" 
                                className="h-7 w-7 object-contain"
                                style={{ filter: 'none' }} loading="lazy" decoding="async"/>
                            </div>
                          )}
                          <div className="relative min-w-0 flex-1">
                            {/* Copy button - visible on hover desktop / always on mobile */}
                            <button
                              onClick={() => copyMessage(message.id, message.text)}
                              className={`absolute -top-2 ${message.sender === 'user' ? '-left-7' : '-right-7'} sm:opacity-0 sm:group-hover:opacity-100 opacity-100 transition-opacity p-1 text-gray-400 hover:text-amber-500 z-10`}
                              title="Copy message"
                              data-testid={`copy-message-${message.id}`}
                            >
                              {copiedId === message.id
                                ? <CheckIcon className="w-3 h-3 text-green-400" />
                                : <Copy className="w-3 h-3" />
                              }
                            </button>
                            <div className={`px-4 py-3 rounded-2xl min-w-0 overflow-hidden ${
                              message.sender === 'user'
                                ? 'bg-gray-800 text-white'
                                : 'bg-gray-800 text-white'
                            }`}>
                            {message.sender === 'user' ? (
                              <div className="space-y-2">
                                {message.attachment && (
                                  <div className="space-y-2">
                                    {/* Image Preview */}
                                    {message.attachment.type.startsWith('image/') && message.attachment.backend_url ? (
                                      <div className="rounded-lg overflow-hidden border-2 border-gray-600">
                                        <img 
                                          src={message.attachment.backend_url} 
                                          alt={message.attachment.name}
                                          className="max-w-full h-auto max-h-64 object-contain cursor-pointer"
                                          onClick={() => window.open(message.attachment.backend_url, '_blank')}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            console.error('Failed to load image:', message.attachment.backend_url);
                                          }} loading="lazy" decoding="async" />
                                      </div>
                                    ) : message.attachment.type.startsWith('video/') && message.attachment.backend_url ? (
                                      /* Video Preview */
                                      <div className="rounded-lg overflow-hidden border-2 border-gray-600">
                                        <video 
                                          controls
                                          className="max-w-full h-auto max-h-64"
                                          src={message.attachment.backend_url}
                                        >
                                          Your browser does not support the video tag.
                                        </video>
                                      </div>
                                    ) : message.attachment.type.startsWith('audio/') && message.attachment.backend_url ? (
                                      /* Audio Player */
                                      <div className="flex items-center gap-2 p-3 bg-gray-700 rounded-lg border border-gray-600">
                                        <audio controls className="flex-1">
                                          <source src={message.attachment.backend_url} type={message.attachment.type} />
                                          Your browser does not support the audio element.
                                        </audio>
                                      </div>
                                    ) : (
                                      /* File Badge with Download Button */
                                      <div className="flex items-center gap-2 p-2 bg-gray-700 rounded-lg border border-gray-600">
                                        {message.attachment.type === 'application/pdf' ? (
                                          <FileText className="w-4 h-4 text-red-400 flex-shrink-0" />
                                        ) : message.attachment.type.startsWith('text/') ? (
                                          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        ) : (
                                          <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        )}
                                        <span className="text-xs text-gray-300 truncate flex-1">{message.attachment.name}</span>
                                        {message.attachment.backend_url && (
                                          <a
                                            href={message.attachment.backend_url}
                                            download={message.attachment.name}
                                            className="text-xs text-orange-400 hover:text-orange-300 underline flex-shrink-0"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Download
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {message.text && (
                                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.text}</p>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm leading-relaxed max-w-full overflow-hidden">
                                <ReactMarkdown
                                  skipHtml={false}
                                  components={{
                                    p: ({ node, children }) => <p className="mb-3 last:mb-0 break-words overflow-wrap-anywhere leading-relaxed">{children}</p>,
                                    strong: ({ node, children }) => <strong className="font-bold gradient-text-gold font-playfair">{children}</strong>,
                                    em: ({ node, children }) => <em className="italic">{children}</em>,
                                    ul: ({ node, children }) => <ul className="list-disc ml-5 mb-3 space-y-1 break-words">{children}</ul>,
                                    ol: ({ node, children }) => <ol className="list-decimal ml-5 mb-3 space-y-1 break-words">{children}</ol>,
                                    li: ({ node, children }) => <li className="break-words overflow-wrap-anywhere leading-relaxed">{children}</li>,
                                    h1: ({ node, children }) => <h1 className="font-bold text-lg mb-3 mt-4 break-words">{children}</h1>,
                                    h2: ({ node, children }) => <h2 className="font-bold text-base mb-2 mt-3 break-words">{children}</h2>,
                                    h3: ({ node, children }) => <h3 className="font-semibold text-base mb-2 mt-3 break-words">{children}</h3>,
                                    h4: ({ node, children }) => <h4 className="font-semibold text-sm mb-2 mt-2 break-words">{children}</h4>,
                                    hr: ({ node }) => <hr className="my-4 border-t border-gray-300" />,
                                    br: ({ node }) => <br className="my-1" />,
                                    code: ({ node, inline, children }) => 
                                      inline 
                                        ? <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono break-all">{children}</code>
                                        : <pre className="bg-gray-200 p-3 rounded text-xs font-mono overflow-x-auto my-3 break-all"><code>{children}</code></pre>,
                                    blockquote: ({ node, children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">{children}</blockquote>,
                                    a: ({ node, children, href }) => <a href={href} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                                    table: ({ node, children }) => <div className="overflow-x-auto my-3"><table className="border-collapse border border-gray-300 text-xs min-w-full">{children}</table></div>,
                                    thead: ({ node, children }) => <thead className="bg-gray-200">{children}</thead>,
                                    tbody: ({ node, children }) => <tbody>{children}</tbody>,
                                    tr: ({ node, children }) => <tr>{children}</tr>,
                                    th: ({ node, children }) => <th className="border border-gray-300 px-3 py-2 font-semibold text-left">{children}</th>,
                                    td: ({ node, children }) => <td className="border border-gray-300 px-3 py-2">{children}</td>,
                                    img: ({ node, src, alt }) => (
                                      <div className="my-4 flex justify-center">
                                        <img 
                                          src={src} 
                                          alt={alt || 'Generated visual'} 
                                          className="max-w-full h-auto rounded-lg shadow-md"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            console.error('Failed to load image:', src);
                                          }} loading="lazy" decoding="async" />
                                      </div>
                                    ),
                                  }}
                                >
                                  {message.text}
                                </ReactMarkdown>
                                
                                {/* Display generated images */}
                                {message.images && message.images.length > 0 && (
                                  <div className="mt-4 space-y-3">
                                    {message.images.map((imageUrl, idx) => (
                                      <div key={idx} className="rounded-lg overflow-hidden border border-gray-200">
                                        <img 
                                          src={`${BACKEND_URL}${imageUrl}`}
                                          alt="AI Generated Visual"
                                          className="w-full h-auto"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            console.error('Failed to load image:', imageUrl);
                                          }} loading="lazy" decoding="async" />
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Display Mermaid diagrams */}
                                {message.mermaidDiagrams && message.mermaidDiagrams.length > 0 && (
                                  <div className="mt-4 space-y-4">
                                    {message.mermaidDiagrams.map((diagram, idx) => (
                                      <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4">
                                        <MermaidDiagram 
                                          chart={diagram}
                                          className="flex justify-center"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {loading && (
                    <div className="flex justify-start px-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gold-primary/20 flex items-center justify-center flex-shrink-0">
                          <img 
                            src="/nerd-nation-logo.png" 
                            alt="Nerd Nation" 
                            className="h-7 w-7 object-contain"
                            style={{ filter: 'none' }} loading="lazy" decoding="async"/>
                        </div>
                        <div className="bg-gray-800 px-4 py-3 rounded-2xl">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gold-primary rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gold-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-gold-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>

          {/* Input Area - flex-shrink-0 at bottom with safe area support */}
          <div className="flex-shrink-0 bg-white border-t border-gray-200" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
              {user && !user?.profile?.completed_questionnaire ? (
                <div className="flex items-center justify-center p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-orange-700 font-medium">Please complete your profile to use the AI advisor</span>
                </div>
              ) : user && !hasUnlimitedAccess && queriesLeft === 0 ? (
                <div className="flex items-center justify-center p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-700 font-medium">Weekly query limit reached. Upgrade to Premium for unlimited access!</span>
                </div>
              ) : (
                <div>
                  {/* Attached file preview */}
                  {attachedFile && (
                    <div className="mb-2 sm:mb-3 flex items-center gap-2 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <FileText className="w-4 h-4 text-gold-primary flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 flex-1 truncate">{attachedFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setAttachedFile(null)}
                        className="text-orange-500 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  <div className="flex gap-1 sm:gap-2">
                    {/* Attachment button with menu */}
                    <div className="relative">
                      <Button
                        type="button"
                        onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                        variant="outline"
                        className="px-2 sm:px-3 h-10 sm:h-auto border-2 border-orange-500 bg-orange-500 text-white hover:bg-orange-600 hover:border-orange-600"
                        title="Attach file"
                      >
                        <Paperclip className="h-5 w-5" />
                      </Button>
                      
                      {/* Attachment menu popup */}
                      {showAttachmentMenu && (
                        <div className="absolute bottom-full left-0 mb-2 bg-white border rounded-lg shadow-xl p-2 space-y-1 z-50 min-w-[150px]">
                          <button
                            type="button"
                            onClick={() => {
                              cameraInputRef.current?.click();
                              setShowAttachmentMenu(false);
                            }}
                            className="flex items-center gap-3 w-full px-3 py-2 hover:bg-orange-50 rounded text-sm"
                          >
                            <Camera className="w-4 h-4 text-orange-500" />
                            <span>Camera</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              fileInputRef.current?.setAttribute('accept', 'image/*');
                              fileInputRef.current?.click();
                              setShowAttachmentMenu(false);
                            }}
                            className="flex items-center gap-3 w-full px-3 py-2 hover:bg-orange-50 rounded text-sm"
                          >
                            <ImageIcon className="w-4 h-4 text-orange-500" />
                            <span>Gallery</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              fileInputRef.current?.setAttribute('accept', '*/*');
                              fileInputRef.current?.click();
                              setShowAttachmentMenu(false);
                            }}
                            className="flex items-center gap-3 w-full px-3 py-2 hover:bg-orange-50 rounded text-sm"
                          >
                            <FileText className="w-4 h-4 text-orange-500" />
                            <span>Files</span>
                          </button>
                        </div>
                      )}
                      
                      {/* Hidden file inputs */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                    
                    <Input
                      ref={inputRef}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything..."
                      className="flex-1 border-2 border-orange-200 focus:border-orange-500 text-sm sm:text-base h-10 sm:h-auto bg-white text-gray-900 placeholder:text-gray-500"
                      disabled={loading}
                      data-testid="ai-chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={loading || (!inputText.trim() && !attachedFile)}
                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-3 sm:px-6 h-10 sm:h-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="send-message-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
    </div>
  );
};

export default AIAdvisor;